import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, HttpError } from '@/api/client';
import type { WalletTransaction, WalletTxType, WalletView } from '@/api/types';
import { BigCTA } from '@/components/BigCTA';
import { ErrorView, formatError } from '@/components/ErrorView';
import { FullPageSpinner } from '@/components/Spinner';
import { showAlert } from '@/sdk/telegram';

const TX_PAGE_SIZE = 30;

const fmt = (n: number) => n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });

const TYPE_LABEL: Record<WalletTxType, string> = {
  SUBMISSION_REWARD: 'Награда за отчёт',
  ELITE_BONUS: 'Elite-бонус',
  DISPUTE_COMPENSATION: 'Компенсация по диспуту',
  WITHDRAWAL_PENDING: 'Вывод (в обработке)',
  WITHDRAWAL_COMPLETED: 'Вывод выполнен',
  WITHDRAWAL_FAILED: 'Вывод не удался',
  PENALTY: 'Штраф',
};

export function WalletPage() {
  const nav = useNavigate();
  const [wallet, setWallet] = useState<WalletView | null>(null);
  const [txs, setTxs] = useState<WalletTransaction[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [phone, setPhone] = useState('+7');
  // One key per withdrawal intent, NOT per tap: a fresh uuid on every retry defeats the
  // server-side replay guard, so a lost response followed by a second tap pays out twice.
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    let off = false;
    Promise.all([api.getWallet(), api.listWalletTransactions(0, TX_PAGE_SIZE)])
      .then(([w, p]) => { if (!off) { setWallet(w); setTxs(p.content); setTxTotal(p.totalElements); } })
      .catch((e) => { if (!off) setErr(e); });
    return () => { off = true; };
  }, []);

  const loadMoreTxs = async () => {
    setLoadingMore(true);
    try {
      const next = await api.listWalletTransactions(txPage + 1, TX_PAGE_SIZE);
      setTxs((prev) => [...prev, ...next.content]);
      setTxTotal(next.totalElements);
      setTxPage(txPage + 1);
    } finally {
      setLoadingMore(false);
    }
  };

  if (err && !wallet) return <div className="page"><ErrorView error={err} /></div>;
  if (!wallet) return <FullPageSpinner />;

  const lim = wallet.limits;
  const available = wallet.balanceRub - wallet.frozenRub;

  const onWithdraw = async () => {
    setWithdrawError(null);
    const raw = amount.replace(',', '.').trim();
    const amt = Number(raw);
    // Mirror the server's rules (WalletController.WithdrawRequest + PayoutService) so the user
    // is told before the round trip, using the limits the server actually reported.
    if (!raw || Number.isNaN(amt) || amt <= 0) { setWithdrawError('Введите сумму'); return; }
    if (!/^\d+([.]\d{1,2})?$/.test(raw)) { setWithdrawError('Не больше двух знаков после запятой'); return; }
    if (amt < lim.minRub) { setWithdrawError(`Минимальная сумма — ${fmt(lim.minRub)} ₽`); return; }
    if (amt > lim.maxRub) { setWithdrawError(`Максимум за раз — ${fmt(lim.maxRub)} ₽`); return; }
    if (amt > available) { setWithdrawError(`Доступно к выводу — ${fmt(available)} ₽`); return; }
    if (amt > lim.dailyRemainingRub) {
      setWithdrawError(
        lim.dailyRemainingRub > 0
          ? `Сегодня осталось ${fmt(lim.dailyRemainingRub)} ₽ из дневного лимита ${fmt(lim.dailyLimitRub)} ₽`
          : `Дневной лимит ${fmt(lim.dailyLimitRub)} ₽ исчерпан. Попробуйте завтра.`,
      );
      return;
    }
    if (!/^\+7\d{10}$/.test(phone)) { setWithdrawError('Телефон в формате +7XXXXXXXXXX'); return; }

    setWithdrawing(true);
    try {
      const result = await api.withdraw({ amountRub: String(amt), phoneE164: phone }, idemKey);
      // Refresh
      const [w, p] = await Promise.all([api.getWallet(), api.listWalletTransactions(0, TX_PAGE_SIZE)]);
      setWallet(w); setTxs(p.content); setTxTotal(p.totalElements); setTxPage(0);
      setShowForm(false); setAmount('');
      setIdemKey(crypto.randomUUID()); // this intent is settled — the next one is a new payout
      // The server's amount, not the typed one: a retry under the same idempotency key replays
      // the stored result regardless of the body, so after a network error on the first attempt
      // an edited amount would be confirmed back to the user while the original sum was paid.
      const paid = result.amountRub.toLocaleString('ru-RU');
      await showAlert(
        result.status === 'WITHDRAWAL_COMPLETED'
          ? `${paid} ₽ отправлены на ${phone}.`
          : `Заявка на ${paid} ₽ принята. Деньги придут на ${phone} в течение нескольких минут.`,
      );
    } catch (e) {
      // Mock provider может вернуть NotImplementedError при отсутствии Т-Банк интеграции
      setWithdrawError(formatError(e));
      if (e instanceof HttpError && e.status === 501) {
        setWithdrawError('Выплаты временно недоступны. Подключаем платёжного провайдера.');
      }
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <div className="page">
      <button
        type="button"
        onClick={() => nav(-1)}
        className="big-cta secondary"
        style={{ alignSelf: 'flex-start', minHeight: 'auto', padding: '8px 14px', fontSize: 14 }}
      >
        ← Назад
      </button>

      <h1 className="page-title">Кошелёк</h1>

      <div className="card balance-block">
        <div className="balance-amount">{fmt(available)} ₽</div>
        <div className="balance-label">Доступно к выводу</div>
        {wallet.frozenRub > 0 && (
          <div style={{ marginTop: 4, color: 'var(--bk-hint)', fontSize: 12 }}>
            ⏳ {fmt(wallet.frozenRub)} ₽ в холде · всего {fmt(wallet.balanceRub)} ₽
          </div>
        )}
        {wallet.payoutsFrozen && (
          <div className="error-banner" style={{ marginTop: 12 }}>
            Выплаты заморожены. Подробности — в профиле.
          </div>
        )}
      </div>

      {!showForm ? (
        <BigCTA
          onClick={() => setShowForm(true)}
          disabled={available < lim.minRub || wallet.payoutsFrozen || lim.dailyRemainingRub <= 0}
        >
          Вывести деньги
        </BigCTA>
      ) : (
        <div className="card">
          {withdrawError && <div className="error-banner" style={{ marginBottom: 12 }}>{withdrawError}</div>}
          <div className="field">
            <div className="field-label">Сумма, ₽</div>
            <input
              className="field-input"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`от ${fmt(lim.minRub)}`}
            />
            <div className="field-hint">
              Доступно {fmt(available)} ₽ · сегодня можно вывести ещё {fmt(lim.dailyRemainingRub)} ₽
              {' '}из {fmt(lim.dailyLimitRub)} ₽
            </div>
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <div className="field-label">Телефон (СБП)</div>
            <input
              className="field-input"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+7XXXXXXXXXX"
            />
            <div className="field-hint">Деньги придут по СБП на указанный номер.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              className="big-cta secondary"
              onClick={() => { setShowForm(false); setWithdrawError(null); }}
              style={{ flex: 1 }}
              disabled={withdrawing}
            >
              Отмена
            </button>
            <button
              className="big-cta"
              onClick={onWithdraw}
              style={{ flex: 1 }}
              disabled={withdrawing}
            >
              {withdrawing ? 'Отправляем…' : 'Подтвердить'}
            </button>
          </div>
        </div>
      )}

      <section>
        <h2 style={{ fontSize: 18, margin: '8px 0' }}>История операций</h2>
        {txs.length === 0 ? (
          <div className="card" style={{ color: 'var(--bk-hint)', textAlign: 'center' }}>
            Операций пока нет.
          </div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            {txs.map((t, i) => {
              const amount = t.amountRub;
              const positive = amount > 0;
              return (
                <div
                  key={t.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: 12,
                    borderTop: i === 0 ? 'none' : '1px solid var(--bk-border)',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{TYPE_LABEL[t.type]}</div>
                    <div style={{ color: 'var(--bk-hint)', fontSize: 12 }}>
                      {new Date(t.createdAt).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  {/* WITHDRAWAL_COMPLETED/FAILED are status markers — the money already moved
                      on the PENDING row, so they carry amount 0. Rendering "0 ₽" in red reads
                      as a failed payout. */}
                  {amount !== 0 && (
                    <div style={{ fontWeight: 700, color: positive ? 'var(--bk-success)' : 'var(--bk-destructive)' }}>
                      {positive ? '+' : ''}{fmt(amount)} ₽
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {txs.length < txTotal && (
          <button
            type="button"
            className="big-cta secondary"
            style={{ marginTop: 12 }}
            onClick={loadMoreTxs}
            disabled={loadingMore}
          >
            {loadingMore ? 'Загружаем…' : `Показать ещё (${txTotal - txs.length})`}
          </button>
        )}
      </section>
    </div>
  );
}
